import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MultiSelect, type FilterConditionValueRenderProps, type MultiSelectOption } from "@tellescope/ui";
import { useTellescopeInfiniteQuery, useUsersQuery } from "@tellescope/tanstack-query";
import { formatName, type FreeformFilter } from "@tellescope/utils";
import { CalendarTemplateFilterMultiSelect } from "./CalendarTemplateFilterMultiSelect";

export type CalendarFilterValueInputProps = Pick<FilterConditionValueRenderProps, "filter" | "onChange">;

const asStringArray = (value: FreeformFilter["value"]): string[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value) return [value];
  return [];
};

/**
 * Value input for the calendar filter's id-valued fields — `templateId`, `locationId`, and
 * `attendees` each get a multi-select of the matching records instead of the filter row's built-in
 * text input. Only render this for those fields; other fields need `renderValueInput` to return
 * `undefined` so `FilterConditionRow` falls back to its built-in inputs.
 */
export const CalendarFilterValueInput = ({ filter, onChange }: CalendarFilterValueInputProps) => {
  const { t } = useTranslation("contacts");

  const { data: users } = useUsersQuery();

  const locationsQuery = useTellescopeInfiniteQuery({
    model: "appointment_locations",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  const userSelectOptions = useMemo<MultiSelectOption[]>(
    () => (users ?? []).map((user) => ({ value: user.id, label: formatName(user) || user.id })),
    [users]
  );

  const locationSelectOptions = useMemo<MultiSelectOption[]>(
    () => (locationsQuery.data ?? []).map((location) => ({ value: location.id, label: location.title })),
    [locationsQuery.data]
  );

  const selectedValues = asStringArray(filter.value);

  if (filter.field === "templateId") {
    return (
      <CalendarTemplateFilterMultiSelect
        className="w-full bg-white text-xs font-light"
        value={selectedValues}
        onChange={(value) => onChange({ ...filter, value })}
      />
    );
  }

  if (filter.field === "locationId") {
    return (
      <MultiSelect
        className="w-full bg-white text-xs font-light"
        minPopoverWidth={240}
        options={locationSelectOptions}
        value={selectedValues}
        onValueChange={(value) => onChange({ ...filter, value })}
        placeholder={t("Select location")}
      />
    );
  }

  if (filter.field === "attendees") {
    return (
      <MultiSelect
        className="w-full bg-white text-xs font-light"
        minPopoverWidth={240}
        options={userSelectOptions}
        value={selectedValues}
        onValueChange={(value) => onChange({ ...filter, value })}
        placeholder={t("Select user")}
      />
    );
  }

  return null;
};
